/**
 * Validates and rewrites the input value to allow only numeric values up to 10 digits.
 */

import { NUMBER_COLUMNS, NUMBER_FILTER_OPTIONS, SCORE_COLUMNS } from "../constants/grid/agGrid";

export const validateNumericInput = (value: string, charLimit: number): string => {
	// Remove all non-numeric characters and limit to 10 digits
	return value.replace(/[^0-9]/g, "").substring(0, charLimit);
};

export const validateStringInput = (value: string, charLimit: number): string => {
	return value.substring(0, charLimit);
};

/**
 * Normalizes the number filter value by removing non-numeric characters
 * and limiting up to 2 decimal places for score columns and allow a single decimal point.
 */
export const normalizeNumberFilterValue = (value: string, field?: string): string => {
	if (NUMBER_COLUMNS.has(field ?? "")) {
		return validateNumericInput(value, value.length);
	}

	if (!SCORE_COLUMNS.has(field ?? "")) {
		return validateNumericInput(value, value.length);
	}

	const normalizedValue = value.replace(/[^0-9.]/g, "");
	const [whole, ...fractionalParts] = normalizedValue.split(".");
	const fractional = fractionalParts.join("").slice(0, 2);

	return fractional ? `${whole}.${fractional}` : fractionalParts.length > 0 ? `${whole}.` : whole;
};

/**
 * Validates and builds the number filter parameters for AG Grid based on the column type.
 */
export const buildNumberFilterParams = (field?: string) => {
	const isScoreColumn = SCORE_COLUMNS.has(field ?? "");

	return {
		defaultOption: "equals",
		filterOptions: NUMBER_FILTER_OPTIONS,
		// Keep client-side validation aligned with the backend's inclusive Between range.
		inRangeInclusive: true,
		// AG Grid v33 allowedCharPattern adds '[]' itself, so we provide the pattern without brackets
		allowedCharPattern: isScoreColumn ? "0-9." : "0-9",
		numberParser: (text: string | null) => {
			const normalized = normalizeNumberFilterValue(String(text ?? ""), field);

			return normalized === "" || normalized === "." ? null : Number(normalized);
		},
	};
};

/**
 * Year-specific utilities
 */
export const sanitizeYearInput = (value: string): string => {
	// keep only digits and limit to 4 characters (YYYY)
	return value.replace(/[^0-9]/g, "").slice(0, 4);
};

export const isValidYear = (value: string | number): boolean => {
	if (value === null || value === undefined) return false;
	const num = typeof value === "string" ? Number(value) : value;
	if (!Number.isFinite(num)) return false;
	// valid year between 1900 and 9999
	return num >= 1900 && num <= 9999;
};
